import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Stack,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Clear as ClearIcon } from "@mui/icons-material";
import dayjs, { Dayjs } from "dayjs";
import {
  InvoiceFilters as IInvoiceFilters,
  InvoiceCategory,
  InvoiceTipo,
  TIPO_DISPLAY_NAMES,
} from "../types/invoice";

interface InvoiceFiltersProps {
  filters: IInvoiceFilters;
  onFiltersChange: (filters: IInvoiceFilters) => void;
  loading: boolean;
}

const InvoiceFilters: React.FC<InvoiceFiltersProps> = ({
  filters,
  onFiltersChange,
  loading,
}) => {
  const [localFilters, setLocalFilters] = useState<IInvoiceFilters>(filters);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Debounce effect - apply filters after 700ms of no changes
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      // Convert mes/ano to data_inicio/data_fim for consistent filtering
      const processedFilters = { ...localFilters };

      if (processedFilters.mes && processedFilters.ano) {
        const month = parseInt(processedFilters.mes, 10);
        const year = processedFilters.ano;
        const firstDay = new Date(Date.UTC(year, month - 1, 1));
        const lastDay = new Date(Date.UTC(year, month, 0));

        processedFilters.data_inicio = firstDay.toISOString().split("T")[0];
        processedFilters.data_fim = lastDay.toISOString().split("T")[0];
        delete processedFilters.mes;
        delete processedFilters.ano;
      } else if (processedFilters.mes && !processedFilters.ano) {
        // If only month is set, use current year as default
        const month = parseInt(processedFilters.mes, 10);
        const year = new Date().getFullYear();
        const firstDay = new Date(Date.UTC(year, month - 1, 1));
        const lastDay = new Date(Date.UTC(year, month, 0));

        processedFilters.data_inicio = firstDay.toISOString().split("T")[0];
        processedFilters.data_fim = lastDay.toISOString().split("T")[0];
        delete processedFilters.mes;
        delete processedFilters.ano;
      } else if (processedFilters.ano && !processedFilters.mes) {
        // If only year is set, clear it to avoid confusion
        delete processedFilters.ano;
      }

      onFiltersChange(processedFilters);
    }, 700);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localFilters]);

  const handleFilterChange = (
    key: keyof IInvoiceFilters,
    value: any,
    immediate: boolean = false
  ) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);

    // Apply immediately for dropdowns and date pickers
    if (immediate) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      onFiltersChange(newFilters);
    }
  };

  const handleClearFilters = () => {
    // Clear debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const emptyFilters: IInvoiceFilters = {};
    setLocalFilters(emptyFilters);
    // Apply immediately without debounce
    onFiltersChange(emptyFilters);
  };

  const hasActiveFilters = Object.keys(localFilters).some(
    (key) =>
      localFilters[key as keyof IInvoiceFilters] !== undefined &&
      localFilters[key as keyof IInvoiceFilters] !== ""
  );

  return (
    <Stack spacing={3}>
      {/* Category Filter */}
      <FormControl fullWidth>
        <InputLabel id="category-filter-label">Categoria</InputLabel>
        <Select
          labelId="category-filter-label"
          label="Categoria"
          value={localFilters.categoria || ""}
          onChange={(e) =>
            handleFilterChange(
              "categoria",
              (e.target.value as InvoiceCategory) || undefined,
              true // Apply immediately
            )
          }
          disabled={loading}
        >
          <MenuItem value="">Todas as categorias</MenuItem>
          <MenuItem value="a_faturar_view">A Faturar</MenuItem>
          <MenuItem value="a_transferir_view">A Transferir</MenuItem>
          <MenuItem value="regular">Regular</MenuItem>
          <MenuItem value="a_faturar">A Faturar (só)</MenuItem>
          <MenuItem value="despesa">Despesa (só)</MenuItem>
          <MenuItem value="despesa_a_faturar">Despesa A Faturar (só)</MenuItem>
        </Select>
      </FormControl>

      {/* Tipo Filter */}
      <FormControl fullWidth>
        <InputLabel id="tipo-filter-label">Tipo</InputLabel>
        <Select
          labelId="tipo-filter-label"
          label="Tipo"
          value={localFilters.tipo || ""}
          onChange={(e) =>
            handleFilterChange(
              "tipo",
              (e.target.value as InvoiceTipo) || undefined,
              true // Apply immediately
            )
          }
          disabled={loading}
        >
          <MenuItem value="">Todos os tipos</MenuItem>
          {Object.entries(TIPO_DISPLAY_NAMES).map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* No Photo Filter */}
      <FormControlLabel
        control={
          <Checkbox
            checked={!!localFilters.sem_foto}
            onChange={(e) =>
              handleFilterChange(
                "sem_foto",
                e.target.checked || undefined,
                true
              )
            }
            disabled={loading}
          />
        }
        label="Sem foto/documento"
      />

      {/* Month/Year Filter */}
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Mês e Ano
        </Typography>
        <Stack spacing={2}>
          <FormControl fullWidth>
            <InputLabel id="month-filter-label">Mês</InputLabel>
            <Select
              labelId="month-filter-label"
              label="Mês"
              value={localFilters.mes || ""}
              onChange={(e) =>
                handleFilterChange("mes", e.target.value || undefined, true)
              }
              disabled={loading}
            >
              <MenuItem value="">Todos os meses</MenuItem>
              <MenuItem value="1">Janeiro</MenuItem>
              <MenuItem value="2">Fevereiro</MenuItem>
              <MenuItem value="3">Março</MenuItem>
              <MenuItem value="4">Abril</MenuItem>
              <MenuItem value="5">Maio</MenuItem>
              <MenuItem value="6">Junho</MenuItem>
              <MenuItem value="7">Julho</MenuItem>
              <MenuItem value="8">Agosto</MenuItem>
              <MenuItem value="9">Setembro</MenuItem>
              <MenuItem value="10">Outubro</MenuItem>
              <MenuItem value="11">Novembro</MenuItem>
              <MenuItem value="12">Dezembro</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="year-filter-label">Ano</InputLabel>
            <Select
              labelId="year-filter-label"
              label="Ano"
              value={localFilters.ano || ""}
              onChange={(e) =>
                handleFilterChange("ano", e.target.value || undefined, true)
              }
              disabled={loading}
            >
              <MenuItem value="">Todos os anos</MenuItem>
              {Array.from(
                { length: 11 },
                (_, i) => new Date().getFullYear() - 5 + i
              ).map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Box>

      {/* Date Range */}
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Período
        </Typography>
        <Stack spacing={2}>
          <DatePicker
            label="Data Início"
            value={
              localFilters.data_inicio ? dayjs(localFilters.data_inicio) : null
            }
            onChange={(date: Dayjs | null) =>
              handleFilterChange(
                "data_inicio",
                date ? date.format("YYYY-MM-DD") : undefined,
                true // Apply immediately
              )
            }
            disabled={loading}
          />
          <DatePicker
            label="Data Fim"
            value={localFilters.data_fim ? dayjs(localFilters.data_fim) : null}
            onChange={(date: Dayjs | null) =>
              handleFilterChange(
                "data_fim",
                date ? date.format("YYYY-MM-DD") : undefined,
                true // Apply immediately
              )
            }
            disabled={loading}
            minDate={
              localFilters.data_inicio
                ? dayjs(localFilters.data_inicio)
                : undefined
            }
          />
        </Stack>
      </Box>

      {/* ID Filter */}
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          ID da Fatura
        </Typography>
        <TextField
          fullWidth
          label="ID Mínimo"
          type="number"
          value={localFilters.id_min || ""}
          onChange={(e) =>
            handleFilterChange(
              "id_min",
              e.target.value ? parseInt(e.target.value) : undefined
            )
          }
          disabled={loading}
          placeholder="Ex: 100"
          inputProps={{ min: 1, step: 1 }}
          helperText="Mostrar faturas com ID igual ou superior"
        />
      </Box>

      {/* NIF Filters */}
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          NIFs
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="NIF Emitente"
            value={localFilters.nif_emitente || ""}
            onChange={(e) =>
              handleFilterChange("nif_emitente", e.target.value || undefined)
            }
            disabled={loading}
            placeholder="123456789"
            inputProps={{ maxLength: 9 }}
          />
          <TextField
            label="NIF Adquirente"
            value={localFilters.nif_adquirente || ""}
            onChange={(e) =>
              handleFilterChange("nif_adquirente", e.target.value || undefined)
            }
            disabled={loading}
            placeholder="123456789"
            inputProps={{ maxLength: 9 }}
          />
        </Stack>
      </Box>

      {/* Value Range */}
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Faixa de Valores (€)
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="Valor Mínimo"
            type="number"
            value={localFilters.valor_min || ""}
            onChange={(e) =>
              handleFilterChange(
                "valor_min",
                e.target.value ? parseFloat(e.target.value) : undefined
              )
            }
            disabled={loading}
            inputProps={{ min: 0, step: 0.01 }}
          />
          <TextField
            label="Valor Máximo"
            type="number"
            value={localFilters.valor_max || ""}
            onChange={(e) =>
              handleFilterChange(
                "valor_max",
                e.target.value ? parseFloat(e.target.value) : undefined
              )
            }
            disabled={loading}
            inputProps={{ min: 0, step: 0.01 }}
          />
        </Stack>
      </Box>

      {/* Clear Filters Button */}
      {hasActiveFilters && (
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<ClearIcon />}
          onClick={handleClearFilters}
          disabled={loading}
          fullWidth
        >
          Limpar Filtros
        </Button>
      )}

      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            {
              Object.keys(localFilters).filter(
                (key) =>
                  localFilters[key as keyof IInvoiceFilters] !== undefined &&
                  localFilters[key as keyof IInvoiceFilters] !== ""
              ).length
            }{" "}
            filtro(s) ativo(s)
          </Typography>
        </Box>
      )}
    </Stack>
  );
};

export default InvoiceFilters;
